import { useContext } from 'react';
import { Container } from 'react-bootstrap';
import { Store } from '../../Store';
import Redirect from '../redirect';
import LoadingPage from '../utilities/LoadingPage';

export enum ROLE {
  ADMIN = 'admin',
  HOST = 'host',
  CUSTOMER = 'customer',
  PUBLIC = 'public',
}
export interface WithAuthenticationProps {
  RequiredRole: ROLE;
  isSignInRequired: boolean;

  children?: JSX.Element;
}

// check if the user is admin
function isUserAdminUser(state: any) {
  return state.isAuthenticated && state.user.roles.includes('admin');
}

// check if the current user is a host and the role requirement is of a host
function isAccessGrantedAsHost(state: any, roleRequired: ROLE) {
  return (
    state.isAuthenticated &&
    roleRequired === ROLE.HOST &&
    state.user.host_roles.length > 0
  );
}

// check if user is authorized to access the current page
function isUserAuthorized(roleRequired: ROLE, state: any): boolean {
  if (roleRequired === ROLE.PUBLIC) {
    return true;
  }
  // admin has access to everything
  if (isUserAdminUser(state)) {
    return true;
  }

  // user much be a host admin
  if (isAccessGrantedAsHost(state, roleRequired)) {
    return true;
  }

  // user is just a customer i.e is logged in
  if (state.isAuthenticated && roleRequired === ROLE.CUSTOMER) {
    return true;
  }

  // deny access
  return false;
}

/**
 * Checks whether to allow user access to a page or event a component.
 *
 */
export default function AuthenticationLayout({
  RequiredRole = ROLE.PUBLIC,
  isSignInRequired = false,
  children,
}: WithAuthenticationProps) {
  const { state } = useContext(Store);

  if (!isSignInRequired || RequiredRole === ROLE.PUBLIC) {
    return <>{children}</>;
  }

  if (!state.isAuthenticated && !state.isAuthenticating) {
    return <Redirect to="/login?goto=" />;
  }

  if (state.isAuthenticated) {
    const isAuthorized = isUserAuthorized(RequiredRole, state);
    if (isAuthorized) {
      return <>{children}</>;
    }

    // show access denied in this case
    if (!isAuthorized) {
      return (
        <Container className="pt-3">
          <h3>It looks like you're logged in with the wrong account</h3>
          <p>
            You're currently logged in with the email address{' '}
            {state?.user?.email}. If you have an account with a different email
            address, please try logging in with that instead.
          </p>
        </Container>
      );
    }
  }
  // show loading indicator in this case
  return (
    <>
      <Container className="Home mt-3">
        <LoadingPage />
      </Container>
    </>
  );
}
